import { useEffect, useMemo, useState } from 'react'

import { CONTEXT_CHANGED_EVENT } from '@/utils/contextChangedEvent'

export interface ContextInfo {
  contextIri: string | null
  contextName: string | null
  contextSlug: string | null
  isCircle: boolean
  isNetwork: boolean
  isUser: boolean
}

const getSlugFromIri = (contextIri: string | null): string | null => {
  if (!contextIri) return null
  const match = contextIri.match(/\/(?:circles|networks)\/([^/]+)/)
  return match?.[1] ?? null
}

const getContextFromStorage = () => {
  if (typeof window === 'undefined') {
    return { contextIri: null, contextName: null }
  }
  return {
    contextIri: localStorage.getItem('contextIri'),
    contextName: localStorage.getItem('contextName'),
  }
}

export const useContext = (): ContextInfo => {
  const [contextIri, setContextIri] = useState<string | null>(
    getContextFromStorage().contextIri
  )
  const [contextName, setContextName] = useState<string | null>(
    getContextFromStorage().contextName
  )

  useEffect(() => {
    const handleContextChanged = () => {
      const { contextIri: newIri, contextName: newName } =
        getContextFromStorage()
      setContextIri(newIri)
      setContextName(newName)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return
      }
      if (event.key !== 'contextIri' && event.key !== 'contextName') {
        return
      }
      handleContextChanged()
    }

    // Listen to custom event for same-tab updates
    window.addEventListener(CONTEXT_CHANGED_EVENT, handleContextChanged)
    // Fallback for cross-tab synchronization when a tab misses Mercure events.
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(CONTEXT_CHANGED_EVENT, handleContextChanged)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return useMemo(() => {
    const isCircle = contextIri ? contextIri.includes('/circles/') : false
    const isNetwork = contextIri ? contextIri.includes('/networks/') : false
    const isUser = !isCircle && !isNetwork

    return {
      contextIri,
      contextName,
      contextSlug: getSlugFromIri(contextIri),
      isCircle,
      isNetwork,
      isUser,
    }
  }, [contextIri, contextName])
}
